import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { AuthContextType, AuthProviderProps, Profile } from "@/types/auth";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PostgrestSingleResponse } from "@supabase/supabase-js";
import { useAnalytics, AnalyticsEvents } from "@/hooks/useAnalytics";
import { useMetaPixel, MetaPixelEvents } from "@/hooks/useMetaPixel";
import { useQueryClient } from "@tanstack/react-query";

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  error: null,
  setProfile: () => {},
  refreshProfile: async () => {},
  signOut: async () => {},
  updateProfile: async () => ({ data: null, error: null, count: null, status: 0, statusText: "" })
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { user, loading: sessionLoading } = useSession();
  const { profile, loading: profileLoading } = useProfile(user);
  const { checkUserTypeAndRedirect } = useAuthRedirect();
  const [error, setError] = useState<Error | null>(null);
  const [profileState, setProfileState] = useState<Profile | null>(null);
  const { sendEvent } = useAnalytics();
  const { sendEvent: sendPixelEvent } = useMetaPixel();
  const queryClient = useQueryClient();
  
  const [prevUserId, setPrevUserId] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      setProfileState(profile);
      
      if (profile.is_trainer && 
          (!profile.subscription_plan || profile.subscription_plan === 'free')) {
        setupTrialPlan();
      }
    }
  }, [profile]);

  useEffect(() => {
    if (user?.id !== prevUserId) {
      if (prevUserId) {
        console.log("User changed, resetting query cache");
        queryClient.clear();
      }
      setPrevUserId(user?.id || null);
    }
  }, [user?.id, prevUserId, queryClient]);

  useEffect(() => {
    if (user && !sessionLoading && profileState) {
      sendEvent(AnalyticsEvents.USER_LOGIN, {
        event_category: 'authentication',
        event_label: profileState.is_trainer ? 'trainer' : 'student',
        user_type: profileState.is_trainer ? 'trainer' : 'student',
      });
      
      sendPixelEvent(MetaPixelEvents.CUSTOM_EVENT, {
        content_name: 'user_login',
        content_category: 'authentication',
        user_type: profileState.is_trainer ? 'trainer' : 'student',
      });

      // Check subscription status after login
      checkSubscriptionStatusAfterLogin();
    }
  }, [user, sessionLoading, profileState]);

  const loading = sessionLoading || profileLoading;

  useEffect(() => {
    if (user && profile) {
      checkUserTypeAndRedirect(user, profile);
    }
  }, [user, profile]);

  const checkSubscriptionStatusAfterLogin = async () => {
    if (!user || !profileState) return;

    try {
      // Check if subscription is expired
      if (profileState.subscription_end_date) {
        const endDate = new Date(profileState.subscription_end_date);
        const now = new Date();
        
        if (endDate < now && (profileState.subscription_status === 'active' || profileState.subscription_status === 'trialing')) {
          console.log('Subscription expired detected on login, updating status');
          
          // Call edge function to update expired subscriptions
          const { error } = await supabase.functions.invoke('check-expired-subscriptions');
          
          if (error) {
            console.error('Error calling check-expired-subscriptions on login:', error);
          } else {
            // Refresh profile to get updated status
            await refreshProfile();
          }
        }
      }
    } catch (error) {
      console.error('Error checking subscription status after login:', error);
    }
  };

  const setupTrialPlan = async () => {
    if (!user) return;
    
    try {
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 14); // 14 dias de trial
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          subscription_plan: 'trial',
          subscription_status: 'trialing',
          subscription_end_date: trialEndDate.toISOString()
        })
        .eq('id', user.id);
      
      if (updateError) throw updateError;
      
      sendEvent(AnalyticsEvents.SUBSCRIPTION_TRIAL_STARTED, {
        event_category: 'subscription',
        event_label: 'trial_started',
        user_id: user.id
      });
      
      sendPixelEvent(MetaPixelEvents.START_TRIAL, {
        content_name: 'trial_plan',
        content_category: 'subscription',
        value: 0,
        currency: 'EUR'
      });
      
      await refreshProfile();
    } catch (err: any) {
      console.error('Erro ao configurar plano trial:', err);
      setError(err);
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (profileError) throw profileError;
      
      if (data) {
        const fullProfileData = {
          ...data,
          email: user.email || ""
        };
        setProfileState(fullProfileData as Profile);
      }
    } catch (err: any) {
      console.error('Error refreshing profile:', err);
      setError(err);
      toast.error("Erro ao atualizar perfil");
    }
  };

  const signOut = async () => {
    try {
      if (profileState) {
        sendEvent(AnalyticsEvents.USER_LOGOUT, {
          event_category: 'authentication',
          event_label: profileState.is_trainer ? 'trainer' : 'student',
          user_type: profileState.is_trainer ? 'trainer' : 'student',
        });
        
        sendPixelEvent(MetaPixelEvents.CUSTOM_EVENT, {
          content_name: 'user_logout',
          content_category: 'authentication',
          user_type: profileState.is_trainer ? 'trainer' : 'student',
        });
      }
      
      queryClient.clear();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setProfileState(null);
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError(err);
      toast.error("Erro ao sair");
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      const error = new Error("User not authenticated");
      setError(error);
      return { data: null, error, count: null, status: 401, statusText: "Unauthorized" } as PostgrestSingleResponse<any>;
    }
    
    try {
      const result = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);
        
      if (result.error) {
        setError(result.error);
        toast.error("Erro ao atualizar perfil");
      } else {
        refreshProfile();
      }
      
      return result as PostgrestSingleResponse<any>;
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err);
      toast.error("Erro ao atualizar perfil");
      return { data: null, error: err, count: null, status: 500, statusText: "Error" } as PostgrestSingleResponse<any>;
    }
  };

  const contextValue: AuthContextType = {
    user,
    loading,
    profile: profileState,
    error,
    setProfile: setProfileState,
    refreshProfile,
    signOut,
    updateProfile
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
